import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { dedupeRequests } from '../api/dedupeRequests'
import { useStore } from '../store/store'
import { Config } from '../utils/config'
import { decodeJwtExp } from '../utils/decodeJwtExp'
import AuthService from './AuthService'

export const BASE_URL: string = Config.API_URL + '/api/v1'

export const api = axios.create({
    baseURL: BASE_URL
})

let isRequested = false

const TEN_MINUTES_MS = 10 * 60 * 1000

const isTokenExpiringSoon = (token: string | null): boolean => {
    const exp = decodeJwtExp(token)
    if (!exp) {
        return false
    }
    return exp * 1000 - Date.now() <= TEN_MINUTES_MS
}

let refreshPromise: ReturnType<typeof AuthService.refreshAccessToken> | null = null

export const getFreshAccessToken = async (): Promise<string> => {
    const accessToken = localStorage.getItem('accessToken')
    if (!isTokenExpiringSoon(accessToken)) {
        return accessToken ?? ''
    }
    refreshPromise ??= AuthService.refreshAccessToken().finally(() => { refreshPromise = null })
    const refreshed = await refreshPromise
    return refreshed?.accessToken ?? accessToken ?? ''
}

const checkTokenAndRequestRefresh = () => {
    getFreshAccessToken().catch((error) => console.error(error))
}

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('accessToken')
    const refreshToken = localStorage.getItem('refreshToken')
    if (token) {
        config.headers.Authorization = `Bearer ${token}`
    }
    console.log('HERE, token: ' + token, 'refreshToken: ' + refreshToken)

    // Простое логирование запроса
    useStore.getState().addDebugLog('request', {
        type: 'REQUEST',
        url: config.url,
        token: token ? 'yes' : 'no'
    })

    if (!isRequested) {
        checkTokenAndRequestRefresh()
        isRequested = true
        setTimeout(() => {
            isRequested = false
        }, 10000)
    }

    return config
})

type RetryableConfig = InternalAxiosRequestConfig & { _retry?: boolean; skipDedupe?: boolean }

const isRetryableAuthError = (error: AxiosError): error is AxiosError & { config: RetryableConfig } => {
    const config = error.config as RetryableConfig | undefined
    const status = error.response?.status
    return !!config
        && !config._retry
        && (status === 400 || status === 403)
        && !config.url?.includes('/auth/refresh-token')
        && !!localStorage.getItem('refreshToken')
}

const retryAfterTokenRefresh = async (error: AxiosError & { config: RetryableConfig }) => {
    error.config._retry = true
    error.config.skipDedupe = true
    refreshPromise ??= AuthService.refreshAccessToken().finally(() => { refreshPromise = null })

    const refreshed = await refreshPromise
    if (!refreshed) {
        throw error
    }

    error.config.headers.set('Authorization', `Bearer ${refreshed.accessToken}`)
    return api(error.config)
}

api.interceptors.response.use(
    (response) => {
        console.log('Response interceptor:' + response.config.url + ' ' + response.status + ' ' + !!response.config.headers['Authorization'])
        return response
    },
    (error: AxiosError) => {
        console.log('Response error interceptor:' + error.config?.url + ' ' + error.response?.status + ' ' + error.config?.headers['Authorization'])

        if (isRetryableAuthError(error)) {
            return retryAfterTokenRefresh(error).catch(() => Promise.reject(error))
        }

        return Promise.reject(error)
    }
)

// Registered last so a deduped/coalesced request settles on the retried
// outcome above, not on the raw pre-retry error.
dedupeRequests(api)